import { store } from '../store/stencil-redux';
import { Feature } from "geojson";
import { isUkhabBaselineForm, isUkhabPresurveyForm } from "./ukhab/ukhab.constants";
import { MapInstanceState } from "../store/map/map.reducer";
import { getAppFeatureIsEnabled, getAppFormsWithGeometry, getAppId } from "../store/selectors";
import { CoreoAttribute, CoreoForm } from "../types";
import AppDatabase from "./db/app-db.service";
import type { MapFeatureFilter } from "./maps.service";
import { MapDataLayer } from "./maps/maps-data-layer";
import { runWhenMapIdle } from "./maps/maps.utils";
import { UKHabService } from "./ukhab/ukhab.service";
import * as mapboxgl from 'mapbox-gl/esm';

const arrayEquals = <T = any>(a: Array<T>, b: Array<T>): boolean => {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index]);
}

export type FeatureStyler = (features: Feature[]) => Feature[];

export class MapFeaturesService {

  public static instance: MapFeaturesService = new MapFeaturesService();

  private constructor() { }

  private featuresLayer: MapDataLayer = new MapDataLayer(false); // Not Clustered
  private clustersLayer: MapDataLayer = new MapDataLayer(true); // Clustered
  private map: mapboxgl.Map;
  private features: Feature[] = [];
  private previewFeatures: Feature[] = [];
  private additionalRecordSources: Set<string> = new Set();

  // Store current state for re-filtering when preview features change
  private currentMapState: MapInstanceState | null = null;
  private currentFeatureFilter: MapFeatureFilter | null = null;

  public async addTo(map: mapboxgl.Map) {
    this.map = map;
    await this.featuresLayer.addTo(map);
    await this.clustersLayer.addTo(map);
  }

  public async loadFeatures(state: MapInstanceState, featureFilter?: MapFeatureFilter) {
    const storeState = store.getState();
    const forms = getAppFormsWithGeometry(storeState);
    const projectId = getAppId(storeState);

    const features: Promise<Feature[]>[] = [];
    for (const form of forms) {
      features.push(this.loadFormFeatures(projectId, form, state));
    }
    this.features = (await Promise.all(features)).flat();
    if (state) {
      await this.filterMapFeatures(state, featureFilter);
    }
  }

  private async loadFormFeatures(projectId: number, form: CoreoForm, state: MapInstanceState) {
    const features = await AppDatabase.instance.records.findProjectMapFeatures(query =>
      query.where('projectId = ?', projectId)
        .where('surveyId = ?', form.id)
        .field('createdAt')
        .field('state')
        .field('userId')
        .field('data.*')
        .join(`records_${form.id}`, 'data', `records.id = data.id`)
    );
    const styler = await this.createFeatureStyler(form, state);
    return styler(features);
  }

  public async createFeatureStyler(form: CoreoForm, mapState?: MapInstanceState): Promise<FeatureStyler> {
    const state = store.getState();
    const projectId = getAppId(state);
    const dataDrivenStyling = getAppFeatureIsEnabled('dataDrivenStyling')(state);
    const ukhabSymbology = mapState?.ukhabSymbology ?? false;
    const showUKHab = dataDrivenStyling && ukhabSymbology === 'standard';

    if (showUKHab && isUkhabBaselineForm(form)) {
      return UKHabService.instance.ukhabBaselineFeatureStyler();
    } else if (showUKHab && isUkhabPresurveyForm(form)) {
      return UKHabService.instance.ukhabPreSurveyFeatureStyler();
    }
    const styler = await this.formFeatureStyler(projectId, form);
    return styler;
  }

  private async getDDSAttributesForForm(form: CoreoForm): Promise<{
    styleAttribute: CoreoAttribute | undefined,
    labelAttribute: CoreoAttribute | undefined,
    labelCollectionAttribute: CoreoAttribute | undefined,
    labelCollectionSourceType?: "value" | "key" | "attribute"
  }> {
    const styleAttribute = form.styleAttributeId ? await AppDatabase.instance.attributes.findById(form.styleAttributeId) : undefined;
    const labelAttribute = form.labelAttributeId ? await AppDatabase.instance.attributes.findById(form.labelAttributeId) : undefined;
    const labelCollectionAttribute = form.labelCollectionAttributeId ? await AppDatabase.instance.attributes.findById(form.labelCollectionAttributeId) : undefined;
    return { styleAttribute, labelAttribute, labelCollectionAttribute, labelCollectionSourceType: form.labelCollectionSourceType };
  }

  private async formFeatureStyler(projectId: number, form: CoreoForm): Promise<FeatureStyler> {

    const { styleAttribute, labelAttribute, labelCollectionAttribute, labelCollectionSourceType } = await this.getDDSAttributesForForm(form);
    if (!(styleAttribute || labelAttribute)) {
      return (features: Feature[]) => features;
    }

    const labelMap: Map<string, string> = new Map<string, string>();
    const iconMap: Map<string, string> = new Map<string, string>();
    const colorMap: Map<string, string> = new Map<string, string>();

    if (labelAttribute && labelAttribute.collectionId) {
      const items = await AppDatabase.instance.items.search(q => q.where('collectionId = ?', labelAttribute.collectionId)).toArray();

      for (const item of items) {
        if ((labelCollectionSourceType === 'attribute' || labelCollectionSourceType === null) && labelCollectionAttribute && item.data) {
          labelMap.set(item.key, item.data[labelCollectionAttribute.path]);
        } else {
          labelMap.set(item.key, item[labelCollectionSourceType ?? 'value']);
        }
      }
    }

    if (styleAttribute) {
      const items = await AppDatabase.instance.items.search(q => q.where('collectionId = ?', styleAttribute.collectionId)).toArray();
      for (const item of items) {
        if (item.icon) {
          iconMap.set(item.key, `${projectId}_${item.id}`);
        }
        if (item.color) {
          colorMap.set(item.key, item.color);
        }
      }
    }

    return (features: Feature[]) => features.map(feature => {
      const styleVal = styleAttribute ? feature.properties[styleAttribute.path] : undefined;
      const icon = (feature.geometry.type === 'Point' || feature.geometry.type === 'MultiPoint') && iconMap.has(styleVal) ? iconMap.get(styleVal) : undefined;
      const label = labelAttribute?.collectionId ? labelMap.get(feature.properties[labelAttribute.path]) : feature.properties[labelAttribute?.path];

      const styledProperties: Record<string, unknown> = {
        ...feature.properties,
        '__label': label ?? '',
      };
      if (icon) {
        styledProperties['__icon'] = icon;
      }
      if (styleAttribute) {
        const color = colorMap.get(styleVal) ?? form.style?.color ?? form.color;
        if (color) {
          styledProperties['__color'] = color;
        }
      }

      return {
        ...feature,
        properties: styledProperties
      };
    });
  }

  async applyMapDataLayers(
    state: MapInstanceState,
    previousState: MapInstanceState,
    featureFilter?: MapFeatureFilter,
    skipFeatureLoadAndFilter = false
  ) {

    const geometricForms = getAppFormsWithGeometry(store.getState());

    this.featuresLayer.clear();
    this.clustersLayer.clear();

    if (geometricForms.length > 0) {
      for (const form of geometricForms) {
        this.featuresLayer.addForm(form);
        this.clustersLayer.addForm(form);
      }

      for (let i = 0; i < state.dataLayers.length; i++) {
        this.featuresLayer.setSort(state.dataLayers[i].sourceId, i + 1);
        this.clustersLayer.setSort(state.dataLayers[i].sourceId, i + 1);
      }

      // If the geometry opacity has changed, update it
      if (state.geometryOpacity !== previousState?.geometryOpacity) {
        this.featuresLayer.setGeometryOpacity(state.geometryOpacity);
        this.clustersLayer.setGeometryOpacity(state.geometryOpacity);
      }

      await this.featuresLayer.update();
      await this.clustersLayer?.update();

      if (!skipFeatureLoadAndFilter) {
        // If we have change data layers, re-filter the data
        if (previousState) {
          const current = state.dataLayers.filter(a => a.enabled).map(a => a.sourceId).sort();
          const previous = previousState?.dataLayers.filter(a => a.enabled).map(a => a.sourceId).sort();

          if (!arrayEquals(current, previous)) {
            await this.filterMapFeatures(state, featureFilter);
          }
        }

        // Switch the UKHab symbology
        if (previousState == null && this.features.length === 0) {
          await this.loadFeatures(state, featureFilter);
        } else if (previousState != null && state.ukhabSymbology !== previousState.ukhabSymbology) {
          // Reload when user toggles UKHab symbology
          await this.loadFeatures(state, featureFilter);
        }
      }
    } else {
      await this.clear();
    }
    this.clustersLayer.move();
    this.featuresLayer.move();
  }

  public async filterMapFeatures(state: MapInstanceState, customFilter?: MapFeatureFilter): Promise<void> {
    // Store current state for re-filtering when preview features change
    this.currentMapState = state;
    this.currentFeatureFilter = customFilter;

    const allEnabled = state.dataLayers.every(a => a.enabled);
    const allDisabled = state.dataLayers.every(a => !a.enabled);
    const formIds = new Set([...state.dataLayers.filter(a => a.enabled).map(d => d.sourceId)]);

    // If there are no forms, and no custom filter function, return now
    if ((allDisabled || (formIds.size === 0)) && typeof customFilter === 'undefined') {
      await this.clear();
      return;
    }

    // Combine database features with preview features
    const allFeatures = [...this.features, ...this.previewFeatures];

    let filtered = allEnabled ? allFeatures : allFeatures.filter(f => formIds.has(f.properties.surveyId));
    if (customFilter) {
      filtered = filtered.filter(customFilter());
    }

    const p1 = this.featuresLayer.setFeatures(filtered);
    const p2 = this.clustersLayer.setFeatures(filtered.map(({ properties, ...f }) => ({
      ...f,
      geometry: properties.center,
      properties: {
        ...properties
      }
    })));
    await Promise.all([p1, p2]);
  }

  updateClustering(cluster: boolean) {
    if (cluster) {
      this.featuresLayer.setZoomRange(12, 24);
      this.clustersLayer.setZoomRange(0, 12);
      this.clustersLayer.show();
    } else {
      this.featuresLayer.setZoomRange(0, 24);
      runWhenMapIdle(this.map, () => this.clustersLayer.hide());
    }
  }

  public getFeatures() {
    return this.features;
  }

  /**
   * Add temporary preview features (e.g. from split operation).
   * These will be included when filtering/displaying features on the map.
   * Call clearPreviewFeatures() when the preview is cancelled or saved.
   */
  public addPreviewFeatures(features: Feature[]): void {
    this.previewFeatures = features;
    // Re-filter to include the new preview features on the map
    if (this.currentMapState) {
      void this.filterMapFeatures(this.currentMapState, this.currentFeatureFilter).catch((err: unknown) => {
        console.error('MapFeaturesService: filterMapFeatures failed after addPreviewFeatures', err);
      });
    }
  }

  /**
   * Clear all preview features from the map.
   * Called when a split operation is cancelled or when records are saved.
   */
  public clearPreviewFeatures(): void {
    this.previewFeatures = [];
    // Re-filter to remove preview features from the map
    if (this.currentMapState) {
      void this.filterMapFeatures(this.currentMapState, this.currentFeatureFilter).catch((err: unknown) => {
        console.error('MapFeaturesService: filterMapFeatures failed after clearPreviewFeatures', err);
      });
    }
  }

  /**
   * Get the current preview features (useful for testing/debugging).
   */
  public getPreviewFeatures(): Feature[] {
    return this.previewFeatures;
  }

  public async clear(): Promise<void> {
    this.featuresLayer.clear();
    this.clustersLayer?.clear();
    await this.featuresLayer.setFeatures([]);
    await this.clustersLayer?.setFeatures([]);
  }

  public setDarkBasemap(isDark: boolean) {
    this.featuresLayer.setDarkBasemap(isDark);
    this.clustersLayer.setDarkBasemap(isDark);
  }

  public registerRecordSource(sourceId: string) {
    this.additionalRecordSources.add(sourceId);
  }

  public unregisterRecordSource(sourceId: string) {
    this.additionalRecordSources.delete(sourceId);
  }

  public hasSource(sourceId: string) {
    return this.featuresLayer.getSourceId() === sourceId ||
      `${this.featuresLayer.getSourceId()}-style` === sourceId ||
      this.clustersLayer.getSourceId() === sourceId ||
      `${this.clustersLayer.getSourceId()}-style` === sourceId ||
      this.additionalRecordSources.has(sourceId);
  }
}
